// =============================================================================
// CONSENT GATE STATE — the ConsentGate screen's pure decision logic. Mirrors
// this app's own house pattern (goodbyeRoute.ts / homeRouteGate.ts /
// driveExportState.ts): a small, framework-free module a component imports
// rather than inlining the logic in JSX, kept directly unit-testable.
//
// ENROLL lane — after OAuth, a user whose consents are missing/stale hits a
// single warm consent screen: links to both docs, one checkbox (a single
// "I've read and accept both" affordance — the mission's own "one checkbox
// or two" leaves room for either shape; ONE checkbox is chosen here since
// both docs are ALWAYS presented and accepted together in this flow, never
// independently — see ConsentGate.tsx's own header comment for why), and an
// explicit Accept button that fires both POSTs.
// =============================================================================

import type { ConsentDocState } from './api';

export type ConsentGatePhase = 'idle' | 'accepting' | 'error' | 'done';

/** The Accept button is enabled only once the user has actually checked the
 *  box AND the gate isn't already mid-submit — never enabled by default
 *  (that would be the dark pattern this mission explicitly forbids: consent
 *  must be an affirmative, explicit action, never a pre-checked or
 *  always-enabled button). */
export function isAcceptEnabled(checked: boolean, phase: ConsentGatePhase): boolean {
  return checked && phase !== 'accepting';
}

/** Whether the gate should intercept at all — mirrors the server's own
 *  `needsAction` field (GET /auth/consents) but re-derives it independently
 *  from the per-doc state list too, so a caller with only `statuses` (no
 *  server-served `needsAction` field, e.g. a test fixture) still resolves
 *  correctly. True the moment any doc is not 'current'. */
export function shouldShowConsentGate(statuses: { state: ConsentDocState }[]): boolean {
  return statuses.some((s) => s.state !== 'current');
}

/** True when at least one doc in the served list is 'stale' — the
 *  grandfather-prompt signal (an existing user who accepted something
 *  before, just not the latest version), purely for the screen's own COPY
 *  branch (a returning user reads a different first line than a brand-new
 *  signup — see ConsentGate.tsx). The actual `via` value POSTed to the
 *  server is derived server-side (classifyConsentVia, apps/api/lib/
 *  consentState.ts) from the user's real acceptance history, never trusted
 *  from this client-side copy decision — this function is presentation-only. */
export function isGrandfatherPrompt(statuses: { state: ConsentDocState }[]): boolean {
  return statuses.some((s) => s.state === 'stale');
}
